import React, { useState } from "react";
import { X, Wand2, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ROLES, generatePassword } from "./shared";

/**
 * Create + edit dialog for a user account. Renders inline form, handles
 * password generation/copy, role-change auto-reactivation, and a MEMBER-
 * specific photographer linker. Called from Users.jsx with:
 *   <UserDialog
 *     initial={{ email, name, role, active, photographer_id, id?, pending_invite? }}
 *     photogs={[…]}
 *     me={currentUser}
 *     onClose={…}
 *     onSaved={…}
 *   />
 */
export default function UserDialog({ initial, photogs, me, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const isEdit = !!initial.id;
  const isSelf = isEdit && me?.id === initial.id;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (isEdit) {
        const payload = {};
        if (form.email !== initial.email) payload.email = form.email;
        if (form.name !== initial.name) payload.name = form.name;
        if (form.role !== initial.role) payload.role = form.role;
        if ((form.active ?? true) !== (initial.active ?? true)) payload.active = form.active;
        if (form.role === "MEMBER") payload.photographer_id = form.photographer_id || null;
        if (form.password) payload.password = form.password;
        if (Object.keys(payload).length === 0) {
          onSaved();
          return;
        }
        await api.patch(`/users/${initial.id}`, payload);
      } else {
        const payload = {
          email: form.email,
          name: form.name,
          role: form.role,
          password: form.password,
          active: form.active !== false,
        };
        if (form.role === "MEMBER") payload.photographer_id = form.photographer_id;
        await api.post(`/users`, payload);
      }
      onSaved();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const generateAndFill = async () => {
    const pw = generatePassword(12);
    setForm((f) => ({ ...f, password: pw }));
    try {
      await navigator.clipboard.writeText(pw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      // clipboard may be blocked — operator can still copy from the field.
    }
  };

  const copyPassword = async () => {
    if (!form.password) return;
    try {
      await navigator.clipboard.writeText(form.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      // clipboard may be blocked — operator can still copy from the field manually
    }
  };

  return (
    <div className="dialog-bg" onClick={onClose}>
      <form className="dialog-panel p-6" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-semibold">{isEdit ? "Edit" : "New"} user</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="form-row">
          <label className="ccs-label">Email *</label>
          <input
            required type="email" className="ccs-input"
            value={form.email || ""}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="user-email-input"
          />
          {isEdit && form.email !== initial.email && (
            <div className="muted-text text-xs mt-1">
              ⚠ Changing the email will sign this user out of any active session — they&rsquo;ll need to log in again with the new address.
            </div>
          )}
        </div>

        <div className="form-row">
          <label className="ccs-label">Name *</label>
          <input required className="ccs-input" value={form.name || ""}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="user-name-input" />
        </div>

        <div className="form-row">
          <label className="ccs-label">Role *</label>
          <select className="ccs-input" value={form.role}
            onChange={(e) => {
              const newRole = e.target.value;
              setForm((f) => ({
                ...f,
                role: newRole,
                // When an admin assigns a role, they intend the user to have
                // access — auto-tick the active flag so changing role doesn't
                // silently leave the user disabled. Skip when the user is
                // still pending their invite (active=false is correct there
                // until they accept) or this is the admin editing themselves.
                active:
                  initial.pending_invite || isSelf ? f.active : true,
              }));
            }}
            disabled={isSelf}
            data-testid="user-role-select"
          >
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {isSelf && (
            <div className="muted-text text-xs mt-1">
              You can&rsquo;t change your own role. Ask another ADMIN to do it.
            </div>
          )}
          {!isSelf && !initial.pending_invite && form.role !== initial.role && initial.active === false && (
            <div
              className="text-xs mt-1 text-amber-400"
              data-testid="role-change-reactivate-hint"
            >
              ↻ Assigning a new role will re-enable this account.
            </div>
          )}
        </div>

        {form.role === "MEMBER" && (
          <div className="form-row">
            <label className="ccs-label">Linked photographer *</label>
            <select required className="ccs-input"
              value={form.photographer_id || ""}
              onChange={(e) => setForm({ ...form, photographer_id: e.target.value })}
              data-testid="user-photographer-select"
            >
              <option value="">Select photographer…</option>
              {photogs.map((p) => (
                <option key={p.id} value={p.id}>{p.photographer_code} — {p.display_name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-row">
          <label className="ccs-label">{isEdit ? "New password (leave blank to keep)" : "Password *"}</label>
          <div className="flex gap-2">
            <input
              type="text"
              minLength="6"
              required={!isEdit}
              className="ccs-input flex-1 mono"
              value={form.password || ""}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={isEdit ? "Leave blank to keep current password" : "Min 6 characters"}
              data-testid="user-password-input"
            />
            <button
              type="button"
              onClick={generateAndFill}
              className="ccs-btn-ghost flex items-center gap-1 text-xs whitespace-nowrap"
              title="Generate a 12-character random password and copy it to your clipboard"
              data-testid="generate-password-button"
            >
              <Wand2 size={14} /> Generate
            </button>
            {form.password && (
              <button
                type="button"
                onClick={copyPassword}
                className="ccs-btn-ghost flex items-center gap-1 text-xs whitespace-nowrap"
                title="Copy password to clipboard"
                data-testid="copy-password-button"
              >
                <Copy size={14} />
              </button>
            )}
          </div>
          {copied && (
            <div className="success-text text-xs mt-1" data-testid="password-copied">
              ✓ Password copied to clipboard — paste it into your message to the user.
            </div>
          )}
        </div>

        <div className="form-row">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="accent-amber-400"
              checked={form.active !== false}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              disabled={isSelf}
              data-testid="user-active-toggle"
            />
            <span className="text-sm">Account is active</span>
          </label>
          <div className="muted-text text-xs mt-1">
            Turn this off to block sign-in without deleting the account &amp; history.
            {isSelf && " (You can't deactivate your own account.)"}
          </div>
        </div>

        {err && <div className="danger-text text-sm">{err}</div>}

        <div className="flex justify-end gap-3 mt-5">
          <button type="button" onClick={onClose} className="ccs-btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="ccs-btn" data-testid="save-user">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
